import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { thumb } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { ArrowRight, Truck, Store, PlayCircle, Palette, Gift as GiftIcon, ShoppingBag } from "lucide-react";

function AddToCartButton({ product, size, testid, label = "Add to cart", dark = true }) {
  const { addItem } = useCart();
  const onClick = () => {
    addItem(product, size, 1);
    toast.success(`${product.title}${size ? ` (${size})` : ""} added to cart`);
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors ${
        dark ? "bg-[#121212] text-[#F2E9D8] hover:bg-black" : "border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8]"
      }`}
    >
      <ShoppingBag size={14} /> {label}
    </button>
  );
}

function KitCard({ p, highlight }) {
  const included = (p.included || "").split("\n").map((s) => s.trim()).filter(Boolean);
  return (
    <div id={`kit-${p.id}`} data-testid={`kit-card-${p.id}`} className={`bg-white border-2 flex flex-col transition-all scroll-mt-32 ${highlight ? "border-[#E0218A] ring-4 ring-[#E0218A]/30" : "border-[#121212]"}`}>
      <div className="aspect-[4/3] overflow-hidden border-b-2 border-[#121212]">
        <img src={thumb(p.image_url, 700)} alt={p.title} className="w-full h-full object-cover" />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif font-black text-2xl text-[#121212] leading-tight">{p.title}</h3>
          <span className="font-serif font-black text-2xl text-[#121212] whitespace-nowrap">${p.price}</span>
        </div>
        {p.description && <p className="mt-3 text-stone-600">{p.description}</p>}

        {included.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#121212]">What's included</p>
            <ul className="mt-2 space-y-1">
              {included.map((it, i) => (
                <li key={i} className="text-sm text-stone-600 flex items-start gap-2"><span className="text-[#C6FF33] mt-0.5">•</span>{it}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {p.pickup && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2E9D8] text-[11px] tracking-[0.15em] uppercase font-bold"><Store size={12} /> Studio pickup</span>}
          {p.shipping && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2E9D8] text-[11px] tracking-[0.15em] uppercase font-bold"><Truck size={12} /> Shipping{p.shipping_fee ? ` +$${p.shipping_fee}` : ""}</span>}
        </div>

        {/* 3 ways to make this */}
        <div className="mt-6 pt-5 border-t border-stone-200">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#121212] mb-3">Three ways to make it{p.open_studio_project ? ` — ${p.open_studio_project}` : ""}</p>
          <div className="flex flex-col gap-2">
            <Link to="/open-studio" data-testid={`kit-os-${p.id}`} className="inline-flex items-center justify-between px-4 py-2.5 bg-[#121212] text-[#F2E9D8] text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-black transition-colors">
              <span className="inline-flex items-center gap-2"><Palette size={14} /> Decorate in Open Studio</span><ArrowRight size={13} />
            </Link>
            {p.price > 0 && <AddToCartButton product={p} testid={`kit-buy-${p.id}`} label="Add Kit To-Go to cart" />}
            {p.tutorial_id && (
              <Link to={`/tutorials/${p.tutorial_id}`} data-testid={`kit-tutorial-${p.id}`} className="inline-flex items-center justify-between px-4 py-2.5 border-2 border-[#121212] text-[#121212] text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors">
                <span className="inline-flex items-center gap-2"><PlayCircle size={14} /> Watch the tutorial</span><ArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MerchCard({ p }) {
  const [size, setSize] = useState(p.sizes?.length ? "" : null);
  const { addItem } = useCart();
  const hasSizes = p.sizes?.length > 0;
  const add = () => {
    if (hasSizes && !size) { toast.error("Please pick a size"); return; }
    addItem(p, size, 1);
    toast.success(`${p.title}${size ? ` (${size})` : ""} added to cart`);
  };
  return (
    <div data-testid={`merch-card-${p.id}`} className="bg-white border-2 border-[#121212] flex flex-col">
      <div className="aspect-square overflow-hidden border-b-2 border-[#121212]">
        <img src={thumb(p.image_url, 600)} alt={p.title} className="w-full h-full object-cover" />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-serif font-black text-xl text-[#121212] leading-tight">{p.title}</h3>
          <span className="font-serif font-black text-xl text-[#121212] whitespace-nowrap">${p.price}</span>
        </div>
        {p.description && <p className="mt-2 text-sm text-stone-600 line-clamp-2">{p.description}</p>}
        {hasSizes && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {p.sizes.map((s) => (
              <button
                key={s}
                data-testid={`merch-size-${p.id}-${s}`}
                onClick={() => setSize(s)}
                className={`px-2.5 py-1 border text-[10px] tracking-[0.1em] uppercase font-bold transition-colors ${
                  size === s ? "border-[#121212] bg-[#121212] text-[#F2E9D8]" : "border-[#121212]/30 text-[#121212]/70 hover:border-[#121212]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="mt-auto pt-5">
          {p.price > 0 ? (
            <button
              data-testid={`merch-buy-${p.id}`}
              onClick={add}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#121212] text-[#F2E9D8] text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-black transition-colors"
            >
              <ShoppingBag size={14} /> Add to cart
            </button>
          ) : (
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#121212]/40">Coming soon</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [kitParam, setKitParam] = useState(null);
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const k = new URLSearchParams(window.location.search).get("kit");
    if (!k || products.length === 0) return;
    setKitParam(k);
    const el = document.getElementById(`kit-${k}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setKitParam(null), 2600);
    return () => clearTimeout(timer);
  }, [products]);
  const kits = products.filter((p) => p.category === "cake_kit");
  const merch = products.filter((p) => p.category === "merch");

  return (
    <div data-testid="shop-page" className="pt-32 pb-24 fade-in">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        {/* Hero */}
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-4">The Cake Society Shop</p>
        <h1 className="font-serif font-black text-5xl sm:text-6xl text-[#121212] max-w-3xl leading-[1.05]">Take the studio home.</h1>
        <p className="mt-6 text-lg text-stone-600 max-w-2xl">
          Cake kits that match our monthly Open Studio drops, Cake Society merch, and gift cards for every kind of maker.
        </p>

        {/* Cake Kits — main focus */}
        <section data-testid="shop-kits" className="mt-20">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-2">Cake Kits</p>
              <h2 className="font-serif font-black text-4xl sm:text-5xl text-[#121212]">Cake Kits To-Go</h2>
            </div>
            <p className="text-stone-600 max-w-md">
              Every kit matches a current Open Studio project — decorate it with us in person, take a kit home, or follow along with the online tutorial.
            </p>
          </div>
          {kits.length === 0 ? (
            <p className="mt-10 text-stone-500">New kits dropping soon.</p>
          ) : (
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {kits.map((p) => <KitCard key={p.id} p={p} highlight={kitParam === p.id} />)}
            </div>
          )}
        </section>

        {/* Merch — secondary */}
        <section data-testid="shop-merch" className="mt-24 pt-16 border-t-2 border-[#121212]">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-2">Merch</p>
          <h2 className="font-serif font-black text-4xl sm:text-5xl text-[#121212]">Wear the culture.</h2>
          {merch.length === 0 ? (
            <p className="mt-10 text-stone-500">Merch coming soon.</p>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
              {merch.map((p) => <MerchCard key={p.id} p={p} />)}
            </div>
          )}
        </section>

        {/* Gift Cards — secondary */}
        <section data-testid="shop-giftcards" className="mt-24">
          <div className="bg-[#121212] text-[#F2E9D8] p-10 sm:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] mb-3 inline-flex items-center gap-2"><GiftIcon size={14} /> Gift Cards</p>
              <h2 className="font-serif font-black text-3xl sm:text-4xl leading-[1.05]">Give the gift of creating.</h2>
              <p className="mt-3 text-[#F2E9D8]/75">Use toward classes, camps, Open Studio, kits, tutorials, and merch. Delivered by email — instantly or on a date you choose.</p>
            </div>
            <Link to="/gift" data-testid="shop-giftcard-cta" className="shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-[#C6FF33] text-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#b3ec1f] transition-colors">
              Buy a Gift Card <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
